import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { notifyApplyQueued } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, CheckCircle2, AlertTriangle, Loader2, Sparkles,
  FileText, ShieldCheck, RotateCcw, X, Download,
  MapPin, CalendarDays, Check, Briefcase, Clock, Wallet, Puzzle,
} from "lucide-react";
import { CHROME_STORE_URL } from "../lib/extension";
import { Button, Loading } from "../components/kit";
import WorkableReview from "../components/WorkableReview";
import ResumePreviewATS from "../components/resume/ResumePreviewATS";
import ResumeEditor from "../components/resume/ResumeEditor";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

// Per-job status PAGE — SAME master-detail shell as the inbox: left = this employer's mailbox
// (may be empty), right = a segmented Application | Message pane. Application defaults on and carries
// the stepper, the review/answer/retry actions, and the full-transparency "what we submitted" receipt.

const STEP_LABEL = ["Preparing", "Ready", "Submitted", "Confirmed"];
const STAGE_META = {
  preparing:       { label: "Preparing",             color: "#0025D8" },
  applying_browser:{ label: "Applying in your browser…", color: "#0025D8" },
  approved:        { label: "Ready to submit",       color: "#047857" },
  account_setup:   { label: "Working on it",         color: "#0025D8" },
  skipped:         { label: "Skipped",               color: "#64748B" },
  // Applied on the employer's own site — we hold the record, we never sent anything.
  applied_manually: { label: "Self-applied",         color: "#7C3AED" },
  ready_to_review: { label: "Ready to review",       color: "#7C3AED" },
  action_needed:   { label: "Action needed",         color: "#E08A00" },
  submitted:       { label: "Submitted",             color: "#2563EB" },
  confirmed:       { label: "Confirmed by employer", color: "#0FA968" },
  failed:          { label: "Couldn't finish",       color: "#E11D48" },
};
const SOURCE_BADGE = {
  profile: ["Profile", "#0025D8"], bank: ["Answer bank", "#7C3AED"], resume: ["Résumé", "#0FA968"],
  ai: ["AI", "#DB2777"], decline: ["Declined", "#64748B"], default_no: ["Default", "#64748B"],
};
const CATS = { verification: ["Verification", "#E08A00"], applied: ["Confirmed", "#0FA968"], other: ["Other", "#64748B"] };
const AV_COLORS = ["#E8A400", "#0075BE", "#B4131E", "#0B7D3E", "#7C3AED", "#0D9488", "#2563EB", "#DB2777"];
const avatarColor = (n) => { let h = 0; for (let i = 0; i < (n || "").length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0; return AV_COLORS[h % AV_COLORS.length]; };

// Company avatar: the resolved favicon logo, falling back to a gradient initial tile (same app-icon style
// as the application tracker + Autopilot cards) when there's no logo or the image fails to load.
function CompanyAvatar({ company, logo, size = 48, radius = "rounded-2xl" }) {
  const [err, setErr] = useState(false);
  const base = `${radius} flex-shrink-0 border border-black/[0.06] shadow-sm ring-1 ring-black/[0.02]`;
  if (logo && !err) {
    return <img src={logo} alt="" loading="lazy" onError={() => setErr(true)}
      className={`${base} object-contain bg-white`}
      style={{ width: size, height: size, padding: Math.max(3, Math.round(size * 0.17)) }} />;
  }
  const c = avatarColor(company || "?");
  return (
    <span className={`${base} grid place-items-center text-white font-bold`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4), background: `linear-gradient(135deg, ${c}, ${c}CC)` }}>
      {(company || "?")[0].toUpperCase()}
    </span>
  );
}
const tint = (hex, a = "18") => hex + a;
const cleanOpts = (opts) => (opts || []).filter((o) => o && o.toLowerCase() !== "select one");
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  const m = Math.floor(s / 60), h = Math.floor(m / 60), d = Math.floor(h / 24);
  return s < 60 ? "now" : m < 60 ? `${m}m` : h < 24 ? `${h}h` : d < 7 ? `${d}d` : new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// A posted date that may be an ISO string OR a vendor string ("Wed Aug 12 2026") — format gracefully.
const fmtDay = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v).slice(0, 24) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Premium application hero: avatar + title + meta row + status pill + an animated 4-step progress
// timeline. Refined/premium (Linear/Stripe): white card, soft shadow, blue accent, subtle motion.
function ApplicationHero({ data, stage }) {
  const meta = STAGE_META[stage] || STAGE_META.preparing;
  const cur = stage === "confirmed" ? 3 : stage === "submitted" ? 2
    : (stage === "ready_to_review" || stage === "action_needed" || stage === "approved" || stage === "applying_browser") ? 1 : 0;
  const failed = stage === "failed";
  const ts = [data.created_at, data.approved_at, data.submitted_at, data.employer_confirmed_at];
  // The role's terms, captured when the application was created — this is the page a candidate opens
  // before an interview, so what the job actually was matters as much as where the application got to.
  // Work mode joins the location it qualifies; anything we never captured is simply absent.
  const chips = [
    (data.location || data.work_mode) && {
      icon: MapPin,
      text: [data.location, data.work_mode].filter(Boolean).join(" · "),
    },
    data.employment_type && { icon: Clock, text: data.employment_type },
    data.salary && { icon: Wallet, text: data.salary },
    data.posted_at && { icon: CalendarDays, text: `Posted ${fmtDay(data.posted_at)}` },
    data.ats_label && { icon: Briefcase, text: data.ats_label },
  ].filter(Boolean);
  return (
    <div className="rounded-[22px] border border-[#E3E3F3] bg-gradient-to-br from-[#EFEFFB] via-[#ECECF8] to-[#E7E7F5] shadow-[0_1px_2px_rgba(16,23,41,.04),0_18px_40px_-24px_rgba(60,50,140,.28)] overflow-hidden">
      <div className="px-5 sm:px-7 pt-6 pb-5">
        <div className="flex items-start gap-4">
          <CompanyAvatar company={data.company} logo={data.logo} size={48} radius="rounded-2xl" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-[21px] sm:text-[23px] leading-tight font-bold tracking-[-0.01em] text-[#0F172A] text-balance">{data.title || "Application"}</h1>
              <span className="text-[11.5px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 whitespace-nowrap"
                style={{ color: meta.color, background: tint(meta.color), boxShadow: `inset 0 0 0 1px ${tint(meta.color, "22")}` }}>{meta.label}</span>
            </div>
            <div className="text-[14px] font-semibold text-[#334155] mt-0.5">{data.company}</div>
            {chips.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                {chips.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                    <c.icon className="w-3.5 h-3.5 text-slate-400" /> {c.text}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Progress timeline */}
      <div className="px-5 sm:px-9 py-5 border-t border-white/60">
        <div className="flex items-start">
          {STEP_LABEL.map((lb, i) => {
            const done = i < cur, isCur = i === cur && !failed, isFail = failed && i === cur;
            const nodeBg = done ? "#0025D8" : isFail ? "#E11D48" : "#fff";
            const nodeShadow = isCur ? "inset 0 0 0 2px #0025D8, 0 0 0 4px #EAEBFA"
              : done ? "0 2px 6px rgba(0,37,216,.28)" : (isFail ? "none" : "inset 0 0 0 1.5px #D6D6EC");
            const connDone = i < cur;
            return (
              <React.Fragment key={lb}>
                <div className="flex flex-col items-center flex-shrink-0" style={{ width: 78 }}>
                  <span className="relative grid place-items-center rounded-full transition-all duration-300"
                    style={{ width: 30, height: 30, background: nodeBg, boxShadow: nodeShadow }}>
                    {isCur && <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "#0025D8", opacity: 0.18 }} />}
                    {done ? <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      : isFail ? <X className="w-4 h-4 text-white" strokeWidth={3} />
                      : <span className="text-[12px] font-bold" style={{ color: isCur ? "#0025D8" : "#98A2B8" }}>{i + 1}</span>}
                  </span>
                  <span className="text-[12px] font-semibold mt-2 text-center" style={{ color: done || isCur ? "#0F172A" : "#98A2B8" }}>{lb}</span>
                  {ts[i] && (done || isCur) && <span className="text-[10.5px] text-muted-foreground mt-0.5" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(ts[i])}</span>}
                  {i === STEP_LABEL.length - 1 && stage === "submitted" && (
                    <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ color: "#B45309", background: "#F59E0B1F" }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#F59E0B" }} /> Waiting on employer
                    </span>
                  )}
                </div>
                {i < STEP_LABEL.length - 1 && (
                  <span className="flex-1 mt-[14px] rounded-full overflow-hidden" style={{ height: 3, background: "#EEF1F8" }}>
                    <span className="block h-full rounded-full transition-all duration-500" style={{ width: connDone ? "100%" : "0%", background: "#0025D8" }} />
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Rich accordion trigger: colored icon tile + title + subtle subtitle, optional right-side badge.
function SectionTrigger({ icon: Icon, tint: tintHex = "#0025D8", title, subtitle, badge }) {
  return (
    <span className="flex items-center gap-3 flex-1 min-w-0 mr-2">
      <span className="w-9 h-9 rounded-[11px] grid place-items-center flex-shrink-0 bg-white shadow-[0_1px_2px_rgba(16,23,41,.08)]">
        <Icon className="w-[18px] h-[18px]" style={{ color: tintHex }} />
      </span>
      <span className="min-w-0 text-left">
        <span className="block text-[13.5px] font-semibold text-[#0F172A] leading-tight">{title}</span>
        {subtitle && <span className="block text-[11.5px] text-muted-foreground truncate mt-0.5">{subtitle}</span>}
      </span>
      {badge && <span className="ml-auto flex-shrink-0">{badge}</span>}
    </span>
  );
}

// Prose styling for the sanitized job-description HTML (tailwind arbitrary child selectors — no plugin).
const JD_PROSE =
  "text-[14px] leading-[1.75] text-[#334155] max-h-[560px] overflow-auto pr-1 " +
  "[&_h1]:text-[17px] [&_h1]:font-bold [&_h1]:text-[#0F172A] [&_h1]:mt-4 [&_h1]:mb-2 " +
  "[&_h2]:text-[16px] [&_h2]:font-bold [&_h2]:text-[#0F172A] [&_h2]:mt-4 [&_h2]:mb-2 " +
  "[&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:text-[#0F172A] [&_h3]:mt-3.5 [&_h3]:mb-1.5 " +
  "[&_h4]:font-semibold [&_h4]:text-[#0F172A] [&_h4]:mt-3 [&_h4]:mb-1 [&_p]:my-2 " +
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-1 " +
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_ol]:space-y-1 [&_li]:pl-0.5 " +
  "[&_a]:text-[#0025D8] [&_a]:underline [&_a]:font-medium " +
  "[&_strong]:font-semibold [&_strong]:text-[#0F172A] [&_b]:font-semibold " +
  "[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground " +
  "[&_table]:w-full [&_table]:my-2 [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1";

// Clean, readable job description — renders the employer's real formatting (sanitized HTML) when we
// have it, else falls back to plain pre-wrapped text. Meta line: location · posted · est. read time.
function JobDescriptionBody({ text, html, location, posted }) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground mb-3 pb-3 border-b border-border/60">
        {location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {location}</span>}
        {posted && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-slate-400" /> Posted {fmtDay(posted)}</span>}
        <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" /> {mins} min read</span>
      </div>
      {html
        ? <div className={JD_PROSE} dangerouslySetInnerHTML={{ __html: html }} />
        : <div className="text-[13.5px] leading-[1.7] text-[#334155] whitespace-pre-wrap max-h-[540px] overflow-auto pr-1">{text}</div>}
    </div>
  );
}

// "What happens next" — a friendly, informational guide shown once the application is submitted.
// We can't see inside the employer's process, so 'Employer reviews' is simply marked in progress.
function WhatHappensNext() {
  const steps = [
    { label: "Employer reviews", note: "In progress", active: true },
    { label: "You'll hear back" },
    { label: "Interview" },
  ];
  const PINK = "#DB2777";
  return (
    <div className="rounded-[18px] border border-[#E7E7F3] bg-white/70 backdrop-blur-sm shadow-[0_1px_2px_rgba(16,23,41,.04)] px-5 sm:px-6 py-4">
      <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground mb-3">What happens next</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-3 gap-x-5">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2.5 min-w-0">
            {s.active
              ? <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: PINK, boxShadow: `0 0 0 4px ${PINK}1A` }} />
              : <span className="w-3 h-3 rounded-full border-2 border-[#D6D6E4] flex-shrink-0" />}
            <div className="min-w-0 leading-tight">
              <div className={`text-[14px] font-semibold truncate ${s.active ? "text-[#0F172A]" : "text-[#98A2B8]"}`}>{s.label}</div>
              {s.note && <div className="text-[12px] font-semibold" style={{ color: PINK }}>{s.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditableQ({ q, i, value, onChange }) {
  const opts = cleanOpts(q.options);
  const isMulti = q.kind === "multiselect";
  // Multi-select answers are stored as a "; "-joined string of selected labels.
  const selected = isMulti ? String(value || "").split(/\s*;\s*/).filter(Boolean) : [];
  const toggle = (o) => {
    const set = new Set(selected);
    set.has(o) ? set.delete(o) : set.add(o);
    onChange([...set].join("; "));
  };
  const inputCls = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30";
  let control;
  if (opts.length === 0) {
    control = (q.kind === "textarea")
      ? <textarea rows={3} className={inputCls} placeholder="Your answer" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      : <input type={q.kind === "date" ? "date" : "text"} className={inputCls} placeholder="Your answer" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  } else if (isMulti) {                                   // checkboxes — pick any number
    control = (
      <div className="space-y-1.5">
        {opts.map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="accent-[#0025D8]" checked={selected.includes(o)} onChange={() => toggle(o)} />
            <span className="text-[#0F172A]">{o}</span>
          </label>
        ))}
      </div>
    );
  } else if (opts.length > 6) {                           // long single-select → dropdown
    control = (
      <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>Select…</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else {                                                // short single-select → radios
    control = (
      <div className="space-y-1.5">
        {opts.map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name={`q_${i}`} value={o} className="accent-[#0025D8]" checked={value === o} onChange={() => onChange(o)} />
            <span className="text-[#0F172A]">{o}</span>
          </label>
        ))}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border p-3.5">
      <div className="text-sm font-medium text-[#0F172A] mb-2.5">{q.question}</div>
      {control}
    </div>
  );
}

function ReadonlyQA({ a }) {
  const src = SOURCE_BADGE[a.source];
  return (
    <div className="py-3 border-b border-border/60 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-semibold text-[#0F172A]">{a.question}</div>
        {src && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap" style={{ color: src[1], background: tint(src[1]) }}>{src[0]}</span>}
      </div>
      <div className="text-[13px] text-muted-foreground mt-1 whitespace-pre-wrap">{a.answer || "—"}</div>
    </div>
  );
}

function Card({ title, icon, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-border bg-white p-5 shadow-sm ${className}`}>
      {title && <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">{icon}{title}</div>}
      {children}
    </div>
  );
}

function EmailView({ emailId }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    let live = true; setFull(null);
    api.get(`/apply/inbox/${emailId}`).then(({ data }) => live && setFull(data)).catch(() => live && setFull({ text: "(Could not load this message.)" }));
    return () => { live = false; };
  }, [emailId]);
  if (!full) return <div className="flex items-center gap-2 text-sm text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div className="max-w-[720px]">
      <div className="rounded-2xl border border-border overflow-hidden bg-white shadow-sm">
        {full.html ? <iframe title="message" sandbox="" srcDoc={full.html} className="w-full bg-white" style={{ height: 460, border: 0 }} />
          : <pre className="p-6 text-sm text-[#0F172A] whitespace-pre-wrap font-sans">{full.text || "(empty)"}</pre>}
      </div>
      <p className="text-[11.5px] text-muted-foreground mt-3">A copy of this was forwarded to your real inbox{full.from ? <> — reply to <b className="text-[#0F172A]">{full.from}</b> from there.</> : "."}</p>
    </div>
  );
}

export default function ApplyStatus() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("app");        // "app" | "msg"
  const [emailId, setEmailId] = useState(null);
  const [dlResume, setDlResume] = useState(false);

  const load = useCallback(() => {
    setData(null); setErr(false);
    api.get(`/apply/status/${draftId}`).then(({ data }) => {
      setData(data);
    }).catch(() => setErr(true));
  }, [draftId]);
  useEffect(() => { load(); }, [load]);

  // Hydrate the editable answer inputs from the LATEST fetch (initial load AND every poll). The poll
  // below only calls setData, so seeding once in load() left the review inputs BLANK until a manual
  // refresh — the poll updated the question labels but not their input values. Merge-fill so we only
  // set keys the candidate hasn't edited, never clobbering an in-progress edit with a poll.
  useEffect(() => {
    if (!data?.answers?.length) return;
    setAnswers((prev) => {
      const next = { ...prev };
      for (const a of data.answers) if (a.question && next[a.question] === undefined) next[a.question] = a.answer;
      return next;
    });
  }, [data]);

  // Keep the page live through the whole lifecycle WITHOUT a manual refresh. Poll while the backend is
  // still working (preparing/approved), AND keep polling briefly after it reaches a review/action stage
  // until that section's content has actually loaded — the stage can flip a beat before the questions /
  // review payload are ready, so the poll that caught the flip can miss them (and then polling would
  // stop, leaving the section stale until a manual refresh). The catch-up window is time-bounded so it
  // never polls forever; the "working" phase stays uncapped (some ATSes prepare slowly).
  const contentReady = (d) => {
    if (!d) return false;
    if (d.stage === "action_needed") return (d.needs_user_questions || []).length > 0;
    if (d.stage === "ready_to_review")
      return !!(d.has_automation_review || (d.answers || []).length
        || (d.submitted_questions || []).length || (d.needs_user_questions || []).length);
    return true;   // preparing/approved/submitted/failed → not a content-lag state
  };
  const working = ["preparing", "approved", "applying_browser"].includes(data?.stage);
  const catchingUp = ["ready_to_review", "action_needed"].includes(data?.stage) && !contentReady(data);
  const shouldPoll = working || catchingUp;
  const catchUpStart = useRef(0);
  useEffect(() => {
    if (!shouldPoll) { catchUpStart.current = 0; return; }
    if (catchingUp) {
      if (!catchUpStart.current) catchUpStart.current = Date.now();
      if (Date.now() - catchUpStart.current > 60000) return;   // stop catch-up after ~60s (safety cap)
    } else {
      catchUpStart.current = 0;                                 // working phase → uncapped
    }
    let alive = true;
    const timer = setTimeout(async () => {
      try { const { data: d } = await api.get(`/apply/status/${draftId}`); if (alive) setData(d); }
      catch { /* keep the last known state */ }
    }, catchingUp ? 2500 : 5000);   // snappier while catching up to the review content
    return () => { alive = false; clearTimeout(timer); };
  }, [shouldPoll, catchingUp, data, draftId]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <p className="text-muted-foreground">This application could not be found.</p>
      <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate("/apply/tracker")}>Back to applications</Button>
    </div>
  );
  if (!data) return <Loading />;

  const stage = data.stage;
  const stageMeta = STAGE_META[stage] || STAGE_META.preparing;
  const answered = (data.answers || []).filter((a) => a && a.question);
  const surfaced = (data.needs_user_questions || []).filter((q) => q && q.question);
  const fs = data.submitted_fields || {};
  const emails = data.emails || [];
  // A cloud-browser engine (Workable) that filled to review owns its own rich review card below.
  const reviewMode = stage === "ready_to_review" && !data.has_automation_review;
  const needsMode = stage === "action_needed" && surfaced.length > 0;
  const failedMode = stage === "failed" || (stage === "action_needed" && surfaced.length === 0);
  const approvedMode = stage === "approved";   // approved but not yet submitted — offer Submit here
  const accountSetupMode = stage === "account_setup";   // being handled behind the scenes (Dayforce)
  // Split in two, because the same stage means opposite things depending on whether the extension
  // exists. WITH it, the application really is open in a tab and the candidate should switch to it.
  // WITHOUT it, nothing was opened and nothing can be: the draft sits in ext_queued indefinitely.
  // Telling that second candidate to "switch to the other tab" sends them looking for something
  // that was never there, and the Continue button re-queues work for an extension that cannot
  // pick it up — so it appears broken rather than absent.
  const browserStage = stage === "applying_browser";
  const needsExtension = browserStage && data?.extension_connected === false;
  const applyingBrowserMode = browserStage && !needsExtension;   // genuinely open in their own tab
  const setAns = (q) => (v) => setAnswers((a) => ({ ...a, [q]: v }));

  // Download the ATS-friendly résumé (the exact version submitted to employers).
  const downloadAtsResume = async () => {
    if (!data?.resume_structured) return;
    setDlResume(true);
    try {
      const res = await api.post("/resume/export/docx",
        { resume_structured: data.resume_structured, name: data.candidate_name || "Resume", template: "ats" },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(data.candidate_name || "resume").replace(/\s+/g, "_")}_resume.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download the résumé."); }
    finally { setDlResume(false); }
  };
  const stdFields = [["Name", `${fs.first_name || ""} ${fs.last_name || ""}`.trim()], ["Email", fs.email], ["Phone", fs.phone], ["LinkedIn", fs.linkedin], ["GitHub", fs.github], ["Portfolio", fs.portfolio]].filter(([, v]) => v);
  // Custom-form ATSes (Zoho/JazzHR) send form-specific fields that don't map to the standard keys —
  // show the real submitted label→value pairs when present, else the standard applicant fields.
  const fields = (data.submitted_answers && data.submitted_answers.length)
    ? data.submitted_answers.map((a) => [a.label, a.value])
    : stdFields;

  const post = async (path, list, okMsg) => {
    const payload = (list || []).map((q) => ({ id: q.id, question: q.question, answer: (answers[q.question] || "").trim() })).filter((a) => a.answer);
    setSaving(true);
    try {
      await api.post(`/apply/draft/${draftId}/${path}`, (path === "retry" || path === "submit" || path === "skip" || path === "resume-browser") ? undefined : { answers: payload });
      // Anything that puts work back on the queue should reach the extension now, not in 90 seconds.
      if (path !== "skip") notifyApplyQueued();
      toast.success(okMsg); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong."); }
    finally { setSaving(false); }
  };
  // Same rule as the tracker: offered only while an application is stuck, and never once the
  // employer has confirmed receipt (stage is "confirmed" then, so failedMode is already false).
  // Marking it stops every lane re-applying — a duplicate to a real employer cannot be withdrawn.
  const markManual = async (applied) => {
    setSaving(true);
    try {
      await api.post(`/apply/draft/${draftId}/applied-manually`, { applied });
      toast.success(applied ? "Got it — we won't apply to this one again." : "We can apply to this one again.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update this application"); }
    finally { setSaving(false); }
  };
  const openEmail = (id) => { setEmailId(id); setView("msg"); };
  const selEmail = emails.find((e) => e.email_id === emailId);

  const SEC = {
    resume:    { bg: "#E9F7EF", bd: "#CFEBDB", ic: "#12A150" },
    jd:        { bg: "#FBEFE0", bd: "#F1DCBF", ic: "#D9822B" },
    submitted: { bg: "#E9ECFB", bd: "#D5DAF5", ic: "#3B5BDB" },
  };
  const details = (data.submitted_details && data.submitted_details.length) ? data.submitted_details.map((a) => [a.label, a.value]) : fields;
  const questions = data.submitted_questions || [];
  const nSubmitted = details.length + questions.length;
  const showReceipt = nSubmitted > 0 || fs.resume_filename || fs.cover_letter;
  const isSubmitted = stage === "submitted" || stage === "confirmed";   // past vs future tense labels

  return (
    <div className="min-h-screen bg-[#EFEFF9]">
      {/* slim, sticky top bar */}
      <header className="sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6 py-2.5 bg-white/85 backdrop-blur border-b border-[#E7E7F3]">
        <button onClick={() => navigate("/apply/tracker")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0F172A] pr-1">
          <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Applications</span>
        </button>
        <CompanyAvatar company={data.company} logo={data.logo} size={32} radius="rounded-[10px]" />
        <div className="min-w-0 hidden sm:block">
          <div className="font-bold tracking-tight text-[14px] text-[#0F172A] truncate leading-tight">{data.company || "Your application"}</div>
          <div className="text-[11.5px] text-muted-foreground truncate">{data.title || ""}</div>
        </div>
        <div className="flex-1" />
        <span className="text-[11.5px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0" style={{ color: stageMeta.color, background: tint(stageMeta.color) }}>{stageMeta.label}</span>
      </header>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        <ApplicationHero data={data} stage={stage} />
        {(stage === "submitted" || stage === "confirmed") && <WhatHappensNext />}

        {/* tabs — Application | Inbox */}
        <div className="inline-flex bg-white border border-[#E7E7F3] rounded-[11px] p-1 gap-1 shadow-sm">
          {[["app", "Application"], ["msg", `Inbox${emails.length ? ` · ${emails.length}` : ""}`]].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} aria-selected={view === v}
              className="px-4 py-1.5 rounded-[8px] text-[13px] font-semibold transition-colors"
              style={view === v ? { background: "#0025D8", color: "#fff", boxShadow: "0 1px 2px rgba(0,37,216,.25)" } : { color: "#6B7690" }}>{label}</button>
          ))}
        </div>

        {view === "msg" ? (
          /* ── Inbox tab ── */
          <div className="max-w-[820px]">{selEmail ? (
            <div className="rounded-2xl border border-[#E7E7F3] bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-border/60 flex items-center gap-2">
                <button onClick={() => setEmailId(null)} className="inline-flex items-center gap-1 text-[13px] font-semibold text-muted-foreground hover:text-[#0F172A]"><ArrowLeft className="w-4 h-4" /> Inbox</button>
                <span className="ml-auto text-[12px] text-muted-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(selEmail.received_at)}</span>
              </div>
              <div className="px-5 py-4">
                <h1 className="text-[18px] leading-snug font-bold tracking-tight text-[#0F172A] mb-1" style={{ textWrap: "balance" }}>{selEmail.subject}</h1>
                <div className="text-[13px] text-muted-foreground mb-3"><b className="text-[#0F172A] font-semibold">{selEmail.from}</b></div>
                <EmailView emailId={selEmail.email_id} />
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[#E7E7F3] bg-white shadow-sm overflow-hidden">
              {emails.length === 0 ? (
                <div className="px-4 py-14 text-center text-[13px] text-muted-foreground">
                  <Mail className="w-7 h-7 mx-auto mb-3 text-slate-300" />
                  No emails from this employer yet.<br />Verification and confirmation emails will appear here.
                </div>
              ) : emails.map((e) => {
                const c = CATS[e.category] || CATS.other;
                return (
                  <button key={e.email_id} onClick={() => openEmail(e.email_id)}
                    className="relative w-full flex items-start gap-3 px-4 py-3.5 border-b border-border/50 last:border-0 text-left transition-colors hover:bg-muted/30">
                    <span className="absolute left-0 top-3 bottom-3 rounded-r" style={{ width: 3, background: c[1] }} />
                    <CompanyAvatar company={e.company || data.company} logo={data.logo} size={36} radius="rounded-[10px]" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        {!e.read && <span className="w-1.5 h-1.5 rounded-full bg-[#0025D8] flex-shrink-0" />}
                        <span className={`text-[13.5px] text-[#0F172A] truncate ${e.read ? "font-semibold" : "font-bold"}`}>{e.company || e.from}</span>
                        <span className="ml-auto text-[11.5px] text-[#98A2B8] flex-shrink-0" style={{ fontVariantNumeric: "tabular-nums" }}>{timeAgo(e.received_at)}</span>
                      </span>
                      <span className="block text-[13px] text-[#3D4761] truncate mt-0.5">{e.subject}</span>
                      <span className="flex mt-1.5"><span className="inline-flex items-center gap-1.5 font-semibold rounded-full" style={{ color: c[1], background: tint(c[1]), fontSize: 10.5, padding: "2px 8px" }}><span className="rounded-full" style={{ width: 6, height: 6, background: c[1] }} />{c[0]}</span></span>
                    </span>
                  </button>
                );
              })}
              {data.employer_account_email && (
                <div className="px-4 py-3 border-t border-border/50 text-[12px] text-muted-foreground flex items-start gap-2">
                  <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                  <span>Applied via your application inbox <b className="text-[#0F172A] font-semibold break-all">{data.employer_account_email}</b> — recruiter mail forwards to your real inbox.</span>
                </div>
              )}
            </div>
          )}</div>
        ) : (
          /* ── Application tab ── */
          <div className="space-y-4">
            {/* Confirmation reference — shown only when the ATS returns a user-friendly one. */}
            {isSubmitted && data.confirmation && (
              <div className="flex items-center gap-2.5 text-[13px] rounded-xl px-3.5 py-2.5 border" style={{ color: "#0B815A", background: tint("#0FA968", "0E"), borderColor: tint("#0FA968", "33") }}>
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">Confirmation number</span>
                <span className="font-mono font-semibold text-[#0F172A] break-all">{data.confirmation}</span>
                <button onClick={() => { navigator.clipboard?.writeText(data.confirmation); toast.success("Copied"); }}
                  className="ml-auto flex-shrink-0 text-[12px] font-semibold text-[#0B815A] hover:underline">Copy</button>
              </div>
            )}
            {/* A cloud-browser attempt didn't confirm — one friendly note; the single action is the
                review card's "Approve & submit" below (no competing Retry button). */}
            {data.has_automation_review && failedMode && (
              <div className="flex items-start gap-2 text-[13px] rounded-xl px-3.5 py-2.5 border"
                style={{ color: "#B54708", background: tint("#F59E0B", "0E"), borderColor: tint("#F59E0B", "40") }}>
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Your last submission didn’t go through. Check your answers below, then press <b>Approve &amp; submit</b> to try again — nothing was sent.</span>
              </div>
            )}
            {/* Fill-to-review card (self-gates); hidden once submitted/confirmed. */}
            {stage !== "submitted" && stage !== "confirmed" && (
              <WorkableReview draftId={draftId} onSubmitted={load} />
            )}
            {stage === "confirmed" && (
              <div className="flex items-center gap-2 text-sm font-medium rounded-xl px-3.5 py-2.5" style={{ color: "#0FA968", background: tint("#0FA968", "14") }}>
                <CheckCircle2 className="w-4 h-4" /> {data.company} confirmed they received your application.
              </div>
            )}
            {accountSetupMode && (
              <Card>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-2 text-sm text-muted-foreground max-w-[70ch]">
                    <Loader2 className="w-4 h-4 text-[#0025D8] animate-spin flex-shrink-0 mt-0.5" />
                    <span>We couldn't complete this application automatically. Our team is looking into it and will try again shortly — we'll email you when it's submitted.</span>
                  </div>
                  <Button variant="secondary" size="sm" disabled={saving} onClick={() => post("skip", null, "Skipped — we'll set things up in the background.")}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Skip for now
                  </Button>
                </div>
              </Card>
            )}
            {needsExtension && (
              <Card>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-2.5 max-w-[64ch]">
                    <span className="w-9 h-9 rounded-xl bg-[#0025D8]/10 grid place-items-center flex-shrink-0">
                      <Puzzle className="w-4 h-4 text-[#0025D8]" />
                    </span>
                    <div>
                      <div className="text-[13px] font-semibold text-[#0F172A]">
                        This employer needs our browser extension
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        They only accept an application sent from your own browser. Your application
                        is ready and waiting — add the extension and it will be submitted
                        automatically, with nothing else for you to do.
                      </p>
                    </div>
                  </div>
                  <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer"
                     className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#0025D8] hover:bg-[#001FB0] text-white px-3.5 py-2 text-xs font-bold transition-colors">
                    <Puzzle className="w-3.5 h-3.5" /> Add to Chrome
                  </a>
                </div>
              </Card>
            )}
            {applyingBrowserMode && (
              <Card>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-2 text-sm text-muted-foreground max-w-[64ch]">
                    <Loader2 className="w-4 h-4 text-[#0025D8] animate-spin flex-shrink-0 mt-0.5" />
                    {/* Deliberately generic. This card is shown for every browser-lane ATS, and they don't
                        behave alike: iCIMS uses an INVISIBLE hCaptcha (no box to tick), Ashby reCAPTCHA v3,
                        SmartRecruiters DataDome. It also used to promise we'd submit automatically, which is
                        untrue on iCIMS — that final click is left to the candidate by design. */}
                    <span>Your application is open in another browser tab — switch to it to finish.
                      Closed it? Reopen it with the button.</span>
                  </div>
                  <Button variant="navy" size="sm" disabled={saving}
                    onClick={() => post("resume-browser", null, "Re-opening your application in the browser…")}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Continue in your browser
                  </Button>
                </div>
              </Card>
            )}
            {/* When a cloud-browser review exists, WorkableReview above is the sole reviewer/submitter —
                suppress this generic card so the candidate never sees two competing action buttons. */}
            {!data.has_automation_review && (reviewMode || needsMode || failedMode || approvedMode) && (
              <Card>
                {approvedMode && (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-2 text-sm text-muted-foreground max-w-[70ch]">
                      <CheckCircle2 className="w-4 h-4 text-[#047857] flex-shrink-0 mt-0.5" />
                      <span>Approved and ready — your résumé and answers are prepared. Submit it to {data.company || "this employer"} with VetraHub.</span>
                    </div>
                    <Button variant="navy" size="sm" disabled={saving} onClick={() => post("submit", null, "Submitting your application.")}>
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Submit with VetraHub
                    </Button>
                  </div>
                )}
                {reviewMode && (
                  <>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#7C3AED] mb-3"><Sparkles className="w-3.5 h-3.5" /> Filled to Review — nothing submitted yet. Edit any answer, then confirm.</div>
                    <div className="grid sm:grid-cols-2 gap-3">{answered.map((a, i) => <EditableQ key={a.id || `a${i}`} q={a} i={i} value={answers[a.question]} onChange={setAns(a.question)} />)}</div>
                    {answered.length === 0 && <div className="text-xs text-muted-foreground italic">No screening questions to answer.</div>}
                    <div className="flex items-center justify-end gap-2 mt-4">
                      <Button variant="secondary" size="sm" onClick={() => navigate("/apply/tracker")}>Not now</Button>
                      <Button variant="navy" size="sm" disabled={saving} onClick={() => post("confirm-submit", answered, "Confirmed — submitting your application.")}>{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Confirm &amp; submit</Button>
                    </div>
                  </>
                )}
                {needsMode && (
                  <>
                    <div className="text-sm text-muted-foreground mb-3">Review the full application below before we retry. The highlighted questions need your answer; the rest we filled for you — edit any if needed. We save everything and retry.</div>
                    {surfaced.length > 0 && (
                      <>
                        <div className="text-xs font-semibold text-[#B54708] mb-2">Needs your answer</div>
                        <div className="grid sm:grid-cols-2 gap-3 mb-4">{surfaced.map((q, i) => <EditableQ key={q.id || `s${i}`} q={q} i={i} value={answers[q.question]} onChange={setAns(q.question)} />)}</div>
                      </>
                    )}
                    {answered.length > 0 && (
                      <>
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#7C3AED] mb-2"><Sparkles className="w-3.5 h-3.5" /> Filled for you — review &amp; edit</div>
                        <div className="grid sm:grid-cols-2 gap-3">{answered.map((q, i) => <EditableQ key={q.id || `a${i}`} q={q} i={i} value={answers[q.question]} onChange={setAns(q.question)} />)}</div>
                      </>
                    )}
                    <div className="flex items-center justify-end mt-4"><Button variant="navy" size="sm" disabled={saving} onClick={() => post("answer-questions", [...surfaced, ...answered], "Answers saved — retrying your application.")}>{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Save &amp; retry</Button></div>
                  </>
                )}
                {failedMode && (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-2 text-sm text-muted-foreground max-w-[70ch]"><AlertTriangle className="w-4 h-4 text-[#E11D48] flex-shrink-0 mt-0.5" /><span>{data.error || "We couldn't complete this employer's application form. Nothing was submitted."}</span></div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {data.applied_manually_at ? (
                        <div className="flex items-center gap-2 text-[13px] rounded-lg px-3 py-1.5 border"
                          style={{ color: "#B45309", background: tint("#F59E0B", "12"), borderColor: tint("#F59E0B", "40") }}>
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                          <span>You applied to this one yourself — we won't send it again.</span>
                          <button onClick={() => markManual(false)} disabled={saving}
                            className="font-semibold underline hover:no-underline disabled:opacity-40">Undo</button>
                        </div>
                      ) : (
                        <>
                          <Button variant="secondary" size="sm" disabled={saving} onClick={() => markManual(true)}
                            title="If you've already applied on the employer's site, tell us so we never send a duplicate">
                            Self-applied
                          </Button>
                          <Button variant="navy" size="sm" disabled={saving} onClick={() => post("retry", null, "Retrying…")}>{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Retry</Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Review sections — full-width color-tinted cards, stacked. Collapsed until clicked. */}
            <Accordion type="multiple" className="space-y-3">
              {/* Your optimized résumé — green */}
              {data.resume_structured && (
                <AccordionItem value="resume" className="rounded-2xl border overflow-hidden bg-white shadow-sm" style={{ borderColor: SEC.resume.bd }}>
                  <AccordionTrigger className="px-4 py-3.5 hover:no-underline hover:brightness-[0.98] transition" style={{ background: SEC.resume.bg }}>
                    <SectionTrigger icon={FileText} tint={SEC.resume.ic} title="Your optimized résumé"
                      subtitle={data.resume_editable ? "Tailored to this job · editable before submit" : `Sent to ${data.company || "the employer"}`}
                      badge={isSubmitted
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-white text-[#0B815A] whitespace-nowrap shadow-sm"><Check className="w-3 h-3" strokeWidth={3} /> ATS · SENT</span>
                        : <span className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-white text-[#0B815A] whitespace-nowrap shadow-sm">ATS</span>} />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 bg-white">
                    {data.resume_editable ? (
                      <>
                        <div className="text-[12.5px] text-muted-foreground mb-3">Edit anything below before you submit — changes save to this application and are exactly what we send. Employers always receive this ATS-friendly version.</div>
                        <ResumeEditor initialData={data.resume_structured} lockTemplate="ats"
                          saveEndpoint={`/apply/status/${draftId}/resume`} candidateName={data.candidate_name} />
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="text-[12.5px] text-muted-foreground">The ATS-friendly résumé submitted to {data.company || "the employer"}.</div>
                          <Button variant="secondary" size="sm" disabled={dlResume} onClick={downloadAtsResume}>
                            {dlResume ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download
                          </Button>
                        </div>
                        <div className="rounded-lg border border-border overflow-auto max-h-[640px] bg-white p-1"><ResumePreviewATS data={data.resume_structured} /></div>
                      </>
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Job description — amber */}
              {data.job_description && (
                <AccordionItem value="jd" className="rounded-2xl border overflow-hidden bg-white shadow-sm" style={{ borderColor: SEC.jd.bd }}>
                  <AccordionTrigger className="px-4 py-3.5 hover:no-underline hover:brightness-[0.98] transition" style={{ background: SEC.jd.bg }}>
                    <SectionTrigger icon={Briefcase} tint={SEC.jd.ic} title="Job description"
                      subtitle={`${Math.max(1, Math.round((data.job_description || "").split(/\s+/).filter(Boolean).length / 200))} min read${data.location ? ` · ${data.location}` : ""}`} />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 bg-white">
                    <JobDescriptionBody text={data.job_description} html={data.job_description_html} location={data.location} posted={data.posted_at} />
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* What we submitted — blue */}
              {showReceipt && (
                <AccordionItem value="submitted" className="rounded-2xl border overflow-hidden bg-white shadow-sm" style={{ borderColor: SEC.submitted.bd }}>
                  <AccordionTrigger className="px-4 py-3.5 hover:no-underline hover:brightness-[0.98] transition" style={{ background: SEC.submitted.bg }}>
                    <SectionTrigger icon={ShieldCheck} tint={SEC.submitted.ic} title={isSubmitted ? "What we submitted" : "What we'll submit"}
                      subtitle={nSubmitted > 0 ? `${nSubmitted} field${nSubmitted !== 1 ? "s" : ""}` : "Résumé + cover letter"} />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 bg-white">
                    {details.length > 0 && (
                      <>
                        <div className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">Your details</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
                          {details.map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-3 py-2 border-b border-border/60 text-sm"><span className="text-muted-foreground">{k}</span><span className="text-[#0F172A] font-semibold truncate">{v}</span></div>
                          ))}
                        </div>
                      </>
                    )}
                    {questions.length > 0 && (
                      <div className="mt-4">
                        <div className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">Screening questions</div>
                        {questions.map((q, i) => (
                          <div key={q.label || i} className="py-2 border-b border-border/60 last:border-0 text-sm">
                            <div className="text-muted-foreground mb-0.5">{q.label}</div>
                            <div className="text-[#0F172A] font-semibold whitespace-pre-wrap">{q.value}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {fs.resume_filename && (
                      <div className="inline-flex items-center gap-2 mt-3.5 px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm font-semibold"><FileText className="w-4 h-4 text-[#0025D8]" /> {fs.resume_filename}{fs.resume_attached && <span className="text-[10.5px] text-[#0FA968] font-bold tracking-wide">ATTACHED</span>}</div>
                    )}
                    {fs.cover_letter && (
                      <details className="mt-3"><summary className="text-sm text-[#0025D8] cursor-pointer">Cover letter</summary><div className="text-sm text-muted-foreground whitespace-pre-wrap mt-2 border-l-2 border-border pl-3">{fs.cover_letter}</div></details>
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>

            {/* Standard-ATS Q&A fallback: only when the unified split produced no questions */}
            {answered.length > 0 && !reviewMode && !(data.submitted_questions && data.submitted_questions.length) && (
              <Card title={`${answered.length} question${answered.length !== 1 ? "s" : ""} answered`}>{answered.map((a, i) => <ReadonlyQA key={a.id || `r${i}`} a={a} />)}</Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
